// src/utils/analysisApi.ts - Complete rewrite for analysis system

import { authenticatedFetch } from './authApi';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AnalysisType {
  type: string;
  description: string;
  supported_data_types: string[];
  supported_devices: string[];
  supported_file_extensions: string[];
  enabled: boolean;
}

export interface CompatibleAnalysisType extends AnalysisType {
  compatible: boolean;
  reason: string;
  file_count: number;
  compatible_files: number;
}

export interface AnalysisSettingsTemplate {
  analysis_type: string;
  default_settings: Record<string, any>;
  parameter_descriptions: Record<string, string>;
  supported_data_types: string[];
  supported_devices: string[];
  supported_file_extensions: string[];
}

export interface Analysis {
  analysis_id: string;
  project_id: string;
  user_id: string;
  analysis_type: string;
  settings: Record<string, any>;
  status: 'configured' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  progress_percentage: number;
  current_step?: string;
  files_processed: number;
  total_files: number;
  result_gcs_path?: string;
  result_file_count: number;
  result_size_bytes: number;
  error_message?: string;
  error_details?: Record<string, any>;
}

export interface AnalysisStatus extends Analysis {
  status_message: string;
  job?: {
    job_id: string;
    status: string;
    cloud_run_job_name?: string;
    cloud_run_execution_name?: string;
    container_logs_uri?: string;
    retry_count: number;
  };
}

export interface AnalysisResults {
  analysis_id: string;
  status: string;
  results_available: boolean;
  result_gcs_path?: string;
  result_file_count: number;
  result_size_bytes: number;
  completed_at?: string;
  download_urls?: Array<{
    filename: string;
    size_bytes: number;
    download_url: string;
  }>;
  message?: string;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
  analysis_id: string;
  analysis_type: string;
}

// ============================================================================
// ANALYSIS TYPE DISCOVERY
// ============================================================================

/**
 * Get all available analysis types
 */
export const getAnalysisTypes = async (): Promise<{
  analysis_types: AnalysisType[];
  count: number;
}> => {
  const response = await authenticatedFetch('/api/analysis/types');
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get analysis types');
  }
  
  return response.json();
};

/**
 * Get detailed information about a specific analysis type
 */
export const getAnalysisTypeDetails = async (analysisType: string): Promise<{
  analysis_type: string;
  template: AnalysisSettingsTemplate;
}> => {
  const response = await authenticatedFetch(`/api/analysis/types/${analysisType}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get analysis type details');
  }
  
  return response.json();
};

/**
 * Get analysis types compatible with a specific project
 */
export const getCompatibleAnalysisTypes = async (projectId: string): Promise<{
  project_id: string;
  compatible_types: CompatibleAnalysisType[];
  count: number;
}> => {
  const response = await authenticatedFetch(`/api/analysis/projects/${projectId}/compatible-types`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get compatible analysis types');
  }
  
  return response.json();
};

// ============================================================================
// ANALYSIS CONFIGURATION & CREATION
// ============================================================================

/**
 * Create a new analysis for a project
 */
export const createAnalysis = async (
  projectId: string,
  params: {
    analysis_type: string;
    settings: Record<string, any>;
  }
): Promise<{
  message: string;
  analysis: Analysis;
}> => {
  const response = await authenticatedFetch(`/api/analysis/projects/${projectId}/analyses`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create analysis');
  }
  
  return response.json();
};

/**
 * Validate analysis settings without creating analysis
 */
export const validateAnalysisSettings = async (
  projectId: string,
  analysisId: string,
  settings: Record<string, any>
): Promise<ValidationResult> => {
  const response = await authenticatedFetch(
    `/api/analysis/projects/${projectId}/analyses/${analysisId}/validate`,
    {
      method: 'POST',
      body: JSON.stringify({ settings }),
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate settings');
  }
  
  return response.json();
};

// ============================================================================
// ANALYSIS EXECUTION
// ============================================================================

/**
 * Execute an analysis
 */
export const executeAnalysis = async (analysisId: string): Promise<{
  message: string;
  analysis_id: string;
  status: AnalysisStatus;
}> => {
  const response = await authenticatedFetch(`/api/analysis/analyses/${analysisId}/execute`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to execute analysis');
  }
  
  return response.json();
};

/**
 * Cancel a running analysis
 */
export const cancelAnalysis = async (analysisId: string): Promise<{
  message: string;
  analysis_id: string;
}> => {
  const response = await authenticatedFetch(`/api/analysis/analyses/${analysisId}/cancel`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel analysis');
  }
  
  return response.json();
};

// ============================================================================
// ANALYSIS STATUS & MONITORING
// ============================================================================

/**
 * Get analysis details
 */
export const getAnalysis = async (analysisId: string): Promise<{
  analysis: Analysis;
}> => {
  const response = await authenticatedFetch(`/api/analysis/analyses/${analysisId}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get analysis');
  }
  
  return response.json();
};

/**
 * Get detailed analysis status
 */
export const getAnalysisStatus = async (analysisId: string): Promise<{
  status: AnalysisStatus;
}> => {
  const response = await authenticatedFetch(`/api/analysis/analyses/${analysisId}/status`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get analysis status');
  }
  
  return response.json();
};

/**
 * Get analysis execution logs
 */
export const getAnalysisLogs = async (analysisId: string): Promise<{
  analysis_id: string;
  logs: string[];
}> => {
  const response = await authenticatedFetch(`/api/analysis/analyses/${analysisId}/logs`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get analysis logs');
  }
  
  return response.json();
};

/**
 * Get all analyses for a project
 */
export const getProjectAnalyses = async (projectId: string): Promise<{
  project_id: string;
  analyses: Analysis[];
  count: number;
}> => {
  // console.log('?? API DEBUG: getProjectAnalyses called for project:', projectId);
  // console.log('?? API DEBUG: Call stack:', new Error().stack);
  
  const response = await authenticatedFetch(`/api/analysis/projects/${projectId}/analyses`);
  
  if (!response.ok) {
    const error = await response.json();
    // console.error('?? API DEBUG: getProjectAnalyses failed:', error);
    throw new Error(error.error || 'Failed to get project analyses');
  }
  
  const result = await response.json();
  // console.log('?? API DEBUG: getProjectAnalyses response:', result);
  return result;
};

// ============================================================================
// ANALYSIS RESULTS
// ============================================================================

/**
 * Get analysis results
 */
export const getAnalysisResults = async (
  analysisId: string,
  includeDownloads: boolean = false
): Promise<{ results: AnalysisResults }> => {
  // console.log('?? API DEBUG: getAnalysisResults called');
  // console.log('?? API DEBUG: analysisId:', analysisId);
  // console.log('?? API DEBUG: includeDownloads:', includeDownloads);
  // console.log('?? API DEBUG: Call stack:', new Error().stack);
  
  const params = new URLSearchParams();
  if (includeDownloads) {
    params.set('include_downloads', 'true');
  }
  
  const url = `/api/analysis/analyses/${analysisId}/results${params.toString() ? '?' + params.toString() : ''}`;
  // console.log('?? API DEBUG: Request URL:', url);
  
  const response = await authenticatedFetch(url);
  
  if (!response.ok) {
    const error = await response.json();
    // console.error('?? API DEBUG: Request failed:', error);
    throw new Error(error.error || 'Failed to get analysis results');
  }
  
  const result = await response.json();
  // console.log('?? API DEBUG: Response data:', result);
  return result;
};

// ============================================================================
// ANALYSIS MANAGEMENT
// ============================================================================

/**
 * Delete an analysis
 */
export const deleteAnalysis = async (analysisId: string): Promise<{
  message: string;
  analysis_id: string;
}> => {
  const response = await authenticatedFetch(`/api/analysis/analyses/${analysisId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete analysis');
  }
  
  return response.json();
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${size.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

/**
 * Get user-friendly status message
 */
export const getStatusMessage = (analysis: Analysis): string => {
  switch (analysis.status) {
    case 'configured':
      return 'Analysis configured and ready to run';
    case 'queued':
      return 'Analysis queued for execution';
    case 'running':
      return `Analysis in progress... ${analysis.progress_percentage.toFixed(1)}% complete`;
    case 'completed':
      return 'Analysis completed successfully';
    case 'failed':
      return `Analysis failed${analysis.error_message ? ': ' + analysis.error_message : ''}`;
    case 'cancelled':
      return 'Analysis was cancelled';
    default:
      return `Analysis status: ${analysis.status}`;
  }
};

/**
 * Get status color for UI
 */
export const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
    case 'queued':
      return 'info';
    case 'cancelled':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Check if analysis can be executed
 */
export const canExecuteAnalysis = (analysis: Analysis): boolean => {
  return analysis.status === 'configured';
};

/**
 * Check if analysis can be cancelled
 */
export const canCancelAnalysis = (analysis: Analysis): boolean => {
  return analysis.status === 'queued' || analysis.status === 'running';
};

/**
 * Check if analysis has results available
 */
export const hasResults = (analysis: Analysis): boolean => {
  return analysis.status === 'completed' && !!analysis.result_gcs_path;
};

/**
 * Poll analysis status - MANUAL POLLING ONLY
 * NOTE: This function is kept for compatibility but should only be used
 * for manual status checks, not automatic background polling
 */
export const pollAnalysisStatus = async (
  analysisId: string,
  onUpdate: (status: AnalysisStatus) => void,
  onComplete: (status: AnalysisStatus) => void,
  onError: (error: Error) => void,
  maxAttempts: number = 10  // Reduced from 200 - only for manual checks
): Promise<void> => {
  let attempts = 0;
  let delay = 2000; // 2 seconds for manual polling
  
  const poll = async () => {
    try {
      attempts++;
      const response = await getAnalysisStatus(analysisId);
      const status = response.status;
      
      onUpdate(status);
      
      // Check if analysis is complete
      const isComplete = ['completed', 'failed', 'cancelled'].includes(status.status) || 
                        status.progress_percentage >= 100;
      
      if (isComplete) {
        console.log(`Manual polling complete: status=${status.status}, progress=${status.progress_percentage}%`);
        onComplete(status);
        return;
      }
      
      // Check if we've reached max attempts (much lower for manual polling)
      if (attempts >= maxAttempts) {
        onError(new Error('Manual status check timeout'));
        return;
      }
      
      // Simple linear delay for manual polling
      setTimeout(poll, delay);
      
    } catch (error) {
      onError(error instanceof Error ? error : new Error('Status polling failed'));
    }
  };
  
  // Start polling
  poll();
};

/**
 * Service health check
 */
export const checkAnalysisServiceHealth = async (): Promise<{
  status: string;
  service: string;
  available_types: number;
  timestamp: string;
}> => {
  const response = await authenticatedFetch('/api/analysis/health');
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Analysis service health check failed');
  }
  
  return response.json();
};